import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from 'components/Button';
import { Heading } from 'components/Heading';
import { MobileScreen } from 'components/MobileScreen';
import { Para } from 'components/Para';
import { RichTextContent } from 'components/RichTextContent';
import { Spacer } from 'components/Spacer';
import { useStore } from 'engine/store';
import { ArrowBigRight, SendHorizonal, Smartphone, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
export const Route = createFileRoute('/showing-rat')({
	component: RouteComponent,
});

function RouteComponent() {
	const [index, setIndex] = useState(0);
	const [showingRat, setShowingRat] = useState(false);
	const setPlayState = useStore((s) => s.setPlayState);
	const navigate = useNavigate();
	const gameShell = document.getElementById('game-shell-container');

	const members = useStore((s) => s.members);
	const ratId = useStore((s) => s.ratId);

	const member = members[index];

	if (!member) {
		console.warn('member not found, what happened', { members, index, ratId });
	}

	useEffect(() => {
		if (gameShell) {
			if (showingRat) {
				gameShell.classList.add('saturate-0');
			} else {
				gameShell.classList.remove('saturate-0');
			}
		}
	}, [gameShell, showingRat]);

	if (members.length < 3) {
		navigate({ to: '/register-members' });
		return; //Stop rendering from happening
	}

	const isRat = ratId === member?.id;

	const TypingAnimation = ({ name }: { name: string }) => {
		const [typedName, setTypedName] = useState('');
		const [animationFinished, setAnimationFinished] = useState(false);

		const typingAudioRef = useRef<HTMLAudioElement>(new Audio('/sfx/widr-crescendo.mp3'));
		const endAudioRef = useRef<HTMLAudioElement>(new Audio('/sfx/widr-flash.mp3'));

		useEffect(() => {
			const interval = setInterval(
				() => {
					if (name.length > typedName.length) {
						setTypedName(name.slice(0, typedName.length + 1));
					}
				},
				3_000 / name.length + 2,
			);

			return () => {
				clearInterval(interval);
			};
		}, [typedName, name]);

		useEffect(() => {
			if (gameShell && typedName.length >= name.length) {
				const timeout = setTimeout(async () => {
					endAudioRef.current.play().then(() => {
						gameShell.classList.remove('saturate-0');
						setAnimationFinished(true);
					});
				}, 2000);
				return () => {
					clearTimeout(timeout);
				};
			} else {
				typingAudioRef.current.play();
			}
		}, [name.length, typedName]);

		return (
			<>
				{!animationFinished ? (
					<div className="flex w-full items-center gap-2">
						<div className="min-h-10 w-full border px-3 py-1 text-lg skin-bg-input skin-border-input skin-text-input skin-rounded-md">
							{typedName}
						</div>
						<div className="border bg-white p-2 leading-none text-black skin-bg-button skin-border-button skin-text-button skin-rounded-md">
							<SendHorizonal className="size-5" />
						</div>
					</div>
				) : (
					<>
						<Heading mode="secondary">
							{isRat ? __`Is rat ${members[index].name}` : __`Is not rat ${members[index].name}`}
						</Heading>
						<RichTextContent content={isRat ? __`Is rat info ${__`Next`}` : __`Is not rat info ${__`Next`}`} />
						<Button
							className="w-full"
							autoFocus={true}
							onClick={() => {
								if (!isRat) {
									gameShell!.classList.remove('brightness-110', 'hue-rotate-[250deg]', 'saturate-[2.1]');
								}
								if (index === members.length - 1) {
									setPlayState('playing');
									navigate({
										to: '/rounds',
										viewTransition: { types: ['slide-left'] },
									});
								} else {
									gameShell!.classList.add('saturate-0');
									setIndex(index + 1);
									setShowingRat(false);
								}
							}}
						>
							{__`Next`}
						</Button>
					</>
				)}
			</>
		);
	};

	return (
		<MobileScreen className="flex flex-col gap-4 mix-blend-difference">
			<div className="flex flex-col gap-4">
				{!showingRat && (
					<>
						<Heading mode="primary">{__`The rat is...?`}</Heading>
						{index === 0 && <Para>{__`Now each team member will get a turn to see if they are the rat.`}</Para>}
						<div>
							<RichTextContent
								content={__`Give the phone to <b>${members[index].name}</b> and press '${__`Next`}' when no one else is looking.`}
							/>
						</div>
						<Spacer />

						<div className="mb-2 mt-8 grid w-full grid-cols-3 text-center md:px-32">
							<Smartphone className="flex size-24 shrink-0" />
							<ArrowBigRight className="flex size-24 shrink-0" />
							<div className="flex min-w-0 flex-col items-center justify-center">
								<User className="size-24" />
							</div>
						</div>
						<Para className="mb-8 w-full truncate text-center text-xl font-bold">{members[index].name}</Para>
						<Button
							className="w-full"
							autoFocus={true}
							onClick={() => {
								gameShell!.classList.add('saturate-0');
								setShowingRat(true);
							}}
						>
							{__`Next`}
						</Button>
					</>
				)}

				{showingRat && (
					<div className="flex flex-col gap-4">
						<Heading mode="primary">{__`${members[index].name}... Are you the rat?`}</Heading>
						<Spacer />
						<img src="images/rat.png" alt="rat" className="mx-auto my-8 w-3/4" />
						<Spacer />
						<TypingAnimation name={members[index].name} />
					</div>
				)}
			</div>
		</MobileScreen>
	);
}
